import {
  Box,
  IconButton,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useDimensions,
} from "@chakra-ui/react"
import { useRouter } from "next/router"
import { useEffect, useMemo, useRef, useState } from "react"
import { keys, pages, sparePartsTableHeaderCodes } from "../../../common/constants/constants"
import { sparePartsAttributes } from "../../../common/constants/search-constants"
import { useBrandedTranslation } from "../../../common/hooks/useBrandedTranslation"
import useIntersectionObserver from "../../../common/hooks/useIntersectionObserver"
import useIsomorphicLayoutEffect from "../../../common/hooks/useIsomorphicLayoutEffect"
import { useSelectedSparePartPosition } from "../../../common/lib/SelectedSparePartPositionProvider"
import { SparePartCellDataType } from "../../../common/types/global"
import { findClosestNumbers } from "../../../common/utils/common-utils"
import CustomLink from "../CustomLink"
import CustomTooltip from "../CustomTooltip"
import { CChevronLeft, CChevronRight } from "../Icons"
import AssetsCell from "./AssetsCell"

const {
  SPARE_PART_POSITION,
  ARTICLE_NUMBER,
  SECONDARY_ARTICLE_NUMBER_1,
  SECONDARY_ARTICLE_NUMBER_2,
  SECONDARY_ARTICLE_NUMBER_3,
  SECONDARY_ARTICLE_NUMBER_4,
  SECONDARY_ARTICLE_NUMBER_5,
  DESCRIPTION,
  REPLACEMENT_FOR,
  REPLACED_BY,
  NOTES,
  ASSETS,
} = sparePartsTableHeaderCodes

// Using theme space.ts values
const columnWidths = {
  [SPARE_PART_POSITION]: 28, //# + the offset to accommodate the scrolling arrow
  [ARTICLE_NUMBER]: 31, //article number
  [SECONDARY_ARTICLE_NUMBER_1]: 30, //secondary article number
  [SECONDARY_ARTICLE_NUMBER_2]: 30, //secondary article number
  [SECONDARY_ARTICLE_NUMBER_3]: 30, //secondary article number
  [SECONDARY_ARTICLE_NUMBER_4]: 30, //secondary article number
  [SECONDARY_ARTICLE_NUMBER_5]: 30, //secondary article number
  [ASSETS]: 30,
  [DESCRIPTION]: 80, //description
  [REPLACEMENT_FOR]: 34, //replacement for
  [REPLACED_BY]: 30, //replaced by
  [NOTES]: 49, //notes
}

const tableShadowStyles = {
  content: '""',
  position: "absolute",
  top: 0,
  bottom: 0,
  transition: "opacity 0.1s ease-in",
  width: "1.5",
}

const iconButtonProps = {
  variant: "link",
  height: "100%",
  width: "56px",
  zIndex: "stickyTableHeaderOptions",
  color: "black",
}

const OFFSET_TOP_TO_SPT = 48
const SCROLL_DIRECTIONS = {
  LEFT: "left",
  RIGHT: "right",
}

type ScrollDirection = (typeof SCROLL_DIRECTIONS)[keyof typeof SCROLL_DIRECTIONS]

type ColDataType = { accessor: string; width: number; Header: string }

type Props = {
  sparePartTable: { rows: SparePartCellDataType[]; columns: { value: string; key: string }[] }
  isExtended?: boolean
  showMoreThreshold?: number
  hasStickyHeader?: boolean
  highlightedArticle?: string
  hasHover?: boolean
  areLinksExternal?: boolean
}

const SparePartsTable = ({
  sparePartTable,
  isExtended = true,
  showMoreThreshold = 0,
  hasStickyHeader = false,
  highlightedArticle = "",
  hasHover = false,
  areLinksExternal = false,
}: Props) => {
  const { t } = useBrandedTranslation()
  const { locale, asPath } = useRouter()
  const scrollableContentEl = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLTableSectionElement>(null)
  const arrowSectionRef = useRef<HTMLDivElement>(null)
  const startOfTableRef = useRef(null)
  const headerRefs = useRef<(HTMLTableCellElement | null)[]>([])
  const scrollStopsRef = useRef<number[]>([])
  const scrollableContentElDimentions = useDimensions(scrollableContentEl, true)
  const [isScrollableLeft, setIsScrollableLeft] = useState<boolean>(false)
  const [isScrollableRight, setIsScrollableRight] = useState<boolean>(false)
  const isDesktop = useBreakpointValue({ base: false, md: true })
  const selectedSparePartPositionContext = useSelectedSparePartPosition()
  // Disable hover effect when we open the modal to avoid flicker from re-rendering
  const isHoverDisabled = useRef(false)
  const updateHoverDisabled = (value: boolean) => (isHoverDisabled.current = value)
  const hasHoverEffects = hasHover && !!isDesktop

  // Define when to show the sticky header based on scroll position
  // 48px the height of the anchor navigation
  const startTableObserverEntry = useIntersectionObserver(startOfTableRef, { rootMargin: `-${OFFSET_TOP_TO_SPT}px` })
  // 88px the height of the anchor navigation + 2px
  const tableContainerObserverEntry = useIntersectionObserver(scrollableContentEl, {
    rootMargin: `-${OFFSET_TOP_TO_SPT + 2}px`,
  })

  const isFixedHeaderVisible = useRef(false)

  isFixedHeaderVisible.current =
    (!startTableObserverEntry?.isIntersecting ?? false) &&
    (tableContainerObserverEntry?.isIntersecting ?? false) &&
    hasStickyHeader

  const showScrollShadows = () => {
    if (scrollableContentEl?.current) {
      const viewportWidth = scrollableContentEl.current.offsetWidth
      const viewportScrollLeft = scrollableContentEl.current.scrollLeft

      // set maxWidth style so the header can be scrolled
      if (headerRef?.current) {
        headerRef.current.style.maxWidth = `${viewportWidth}px`
        // update the scroll value for the header when we scroll the table
        headerRef.current.scrollLeft = scrollableContentEl.current.scrollLeft
      }

      // set maxWidth style so the arrows are horizontally aligned
      if (arrowSectionRef?.current) {
        arrowSectionRef.current.style.maxWidth = `${viewportWidth}px`
        // set the height of the arrow section to match the header height
        // minus 1 so that the border of the header is visible
        // so the arrows are positioned vertically aligned
        arrowSectionRef.current.style.height = `${headerRef.current?.offsetHeight ?? 0}px`
      }

      setIsScrollableLeft(viewportScrollLeft > 0)
      setIsScrollableRight(Math.ceil(viewportScrollLeft + viewportWidth) != scrollableContentEl.current.scrollWidth)
    }
  }

  const handleScrollStep = (direction: ScrollDirection) => {
    const scrollableElement = scrollableContentEl?.current
    if (!scrollableElement) return
    const currentScroll = scrollableElement.scrollLeft
    const [previuosScrollStop, nextScrollStop] = findClosestNumbers(currentScroll, scrollStopsRef.current)
    const scrollAmount = direction === SCROLL_DIRECTIONS.LEFT ? previuosScrollStop : nextScrollStop

    scrollableElement.scrollTo({ left: scrollAmount, behavior: "smooth" })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.preventDefault()
    const scrollableElement = scrollableContentEl?.current
    if (!scrollableElement) return
    const direction =
      e.key === keys.LEFT_ARROW ? SCROLL_DIRECTIONS.LEFT : e.key === keys.RIGHT_ARROW ? SCROLL_DIRECTIONS.RIGHT : null
    if (!direction) return
    handleScrollStep(direction)
  }

  // eslint-disable-next-line
  const data = useMemo(() => sparePartTable?.rows, [asPath])

  // Array with all possible columns for this spare parts table
  const columnsArr =
    sparePartTable?.columns.map((el: { value: string; key: string }) => ({
      Header: el.value,
      accessor: el.key,
      width: columnWidths[el.key],
    })) ?? []

  // Array with all columns that have atleast one cell with data for this spare parts table
  const columns = useMemo(() => {
    return (
      columnsArr
        .map((col: ColDataType) => {
          // Look for atleast one cell with data for this col.accessor
          const hasData = data.find((data: SparePartCellDataType) => data.data[col.accessor] !== undefined)
          return hasData ? col : null
        })
        // Filter out the empty columns
        .filter((el: ColDataType | null) => el != null)
    )

    // eslint-disable-next-line
  }, [columnsArr.length])

  useEffect(() => {
    showScrollShadows()
  }, [scrollableContentElDimentions?.borderBox?.width, isFixedHeaderVisible])

  // Reset shadows and scroll bar of the table element when we change the asPath
  useEffect(() => {
    if (scrollableContentEl?.current) {
      scrollableContentEl.current.scrollLeft = 0
      showScrollShadows()
    }
  }, [asPath])

  useIsomorphicLayoutEffect(() => {
    //we are not interested in the first two columns
    //as they are not scrollable
    const headerCells = headerRefs.current.slice(2)
    const widths = headerCells.map((el) => el?.offsetWidth ?? 0)

    //calculate the valid scroll stops based on the column widths
    //each scroll stop is the sum of the widths of the columns before it + the width of the current column
    //for example, if the widths are [120, 120, 120, 320, 136]
    //the scroll stops are [120, 240, 360, 680, 816]
    const scrollStops: number[] = widths.reduce<number[]>((acc, width, i) => {
      acc.push((acc[i - 1] || 0) + width)
      return acc
    }, [])

    // Add the initial position (0) to the scroll stops
    //result is [0, 120, 240, 360, 680, 816]
    scrollStops.unshift(0)
    scrollStopsRef.current = scrollStops
  }, [])

  return (
    <Box position="relative">
      {/* Dummy div for the beginning of the table to attach the observer to it */}
      <Box ref={startOfTableRef} />

      <TableContainer
        tabIndex={1}
        whiteSpace="normal"
        ref={scrollableContentEl}
        onScroll={showScrollShadows}
        onKeyDown={handleKeyDown}
        _before={
          isScrollableLeft
            ? {
                ...tableShadowStyles,
                boxShadow: "scrollingTableLeft",
                left: columnWidths.ATT_459152 + columnWidths.ATT_10670,
                opacity: isScrollableLeft ? 1 : 0,
                zIndex: "tileContent",
              }
            : {}
        }
        _after={{
          ...tableShadowStyles,
          boxShadow: "scrollingTableRight",
          right: "0",
          opacity: isScrollableRight ? 1 : 0,
        }}
      >
        {(isScrollableLeft || isScrollableRight) && (
          <Box
            as="section"
            ref={arrowSectionRef}
            width="100%"
            position={isFixedHeaderVisible.current ? "fixed" : "absolute"}
            top={isFixedHeaderVisible.current ? `${OFFSET_TOP_TO_SPT}px` : "0"}
            zIndex="4"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            borderBottom="1px"
            borderColor="gray.300"
            borderStyle="solid"
          >
            <IconButton
              {...iconButtonProps}
              aria-label="Scroll left"
              icon={<CChevronLeft />}
              isDisabled={!isScrollableLeft}
              onClick={() => handleScrollStep("left")}
            />
            <IconButton
              {...iconButtonProps}
              aria-label="Scroll right"
              icon={<CChevronRight />}
              isDisabled={!isScrollableRight}
              background="white"
              onClick={() => handleScrollStep("right")}
            />
          </Box>
        )}
        <Table
          variant="inverseStriped"
          fontSize="sm"
          position="relative"
          width="100%"
          // Add room for the header to go back to when we are not scrolling
          mt={isFixedHeaderVisible.current ? "10" : "0"}
        >
          <Thead
            ref={headerRef}
            //apply border to the header only when the table is not scrollable
            //as there is a weird chrome behaviour with sticky td and border-collapse
            borderBottom={!(isScrollableLeft || isScrollableRight) ? "1px" : 0}
            borderColor="gray.300"
            borderStyle="solid"
            position={isFixedHeaderVisible.current ? "fixed" : "initial"}
            top="12"
            overflowX="hidden"
            zIndex="stickyTableHeader"
            width="100%"
          >
            <Tr>
              {columns?.map((col: ColDataType, index: number) => {
                return (
                  <Th
                    key={col.accessor}
                    fontSize="sm"
                    minW={col.width}
                    w={col.accessor === sparePartsAttributes.description ? "100%" : col.width}
                    px="4"
                    paddingLeft={index === 0 ? "14" : "4"}
                    boxShadow="tableBorderShadow"
                    _last={{ boxShadow: "none" }}
                    position={index < 2 ? "sticky" : "inherit"}
                    left={index === 0 ? 0 : columnWidths.ATT_459152}
                    bgColor="white"
                    backgroundClip="padding-box"
                    ref={(el) => (headerRefs.current[index] = el)}
                  >
                    {col.Header}
                  </Th>
                )
              })}
            </Tr>
          </Thead>
          <Tbody>
            {data?.map((cellData: SparePartCellDataType, rowIndex: number) => {
              const shouldBeHighlighted = highlightedArticle === cellData.id.split("_").at(-1)
              return (
                <Tr
                  key={rowIndex}
                  // We have the hover effect only for SPDP page
                  _hover={hasHoverEffects ? { td: { bgColor: "gray.200" }, cursor: "pointer" } : {}}
                  onMouseEnter={() =>
                    hasHoverEffects &&
                    !isHoverDisabled.current &&
                    selectedSparePartPositionContext?.setSelectedSparePartPosition?.(
                      cellData.data[sparePartsTableHeaderCodes.SPARE_PART_POSITION]
                    )
                  }
                  onMouseLeave={() =>
                    hasHoverEffects &&
                    !isHoverDisabled.current &&
                    selectedSparePartPositionContext?.setSelectedSparePartPosition?.("")
                  }
                >
                  {columns?.map((col: ColDataType, colIndex: number) => {
                    if (!isExtended && rowIndex >= showMoreThreshold) return

                    const isArticleLinkAvailable =
                      cellData.available &&
                      col.accessor === sparePartsAttributes.articleNumber &&
                      cellData.type === sparePartsAttributes.articleType

                    const isSPTLinkAvailable =
                      cellData.available &&
                      col.accessor === sparePartsAttributes.description &&
                      cellData.type === sparePartsAttributes.sptType

                    // Can be a Prodcuct id or a Spare part id
                    const itemId = isArticleLinkAvailable ? cellData.id.split("_").slice(0, -1).join("_") : cellData.id

                    const isMedia = col.accessor === ASSETS

                    return (
                      <Td
                        key={col.accessor + rowIndex + colIndex}
                        left={colIndex === 0 ? 0 : columnWidths.ATT_459152}
                        position={colIndex < 2 ? "sticky" : "inherit"}
                        zIndex={colIndex < 2 ? "stickyTile" : 0}
                        bgColor={rowIndex % 2 ? "gray.50" : "white"}
                        boxShadow="tableBorderShadow"
                        _last={{ boxShadow: "none" }}
                        px="4"
                        py="2.5"
                        paddingLeft={colIndex === 0 ? "14" : "4"}
                        minW={col.width}
                        w={col.accessor === sparePartsAttributes.description ? "100%" : col.width}
                        verticalAlign="top"
                        _first={{
                          _before: {
                            content: '""',
                            display: shouldBeHighlighted ? "block" : "none",
                            borderLeft: shouldBeHighlighted ? "solid" : "",
                            borderWidth: "4px",
                            borderColor: "warning",
                            width: "4px",
                            position: "absolute",
                            top: "4px",
                            bottom: "4px",
                            left: 0,
                          },
                        }}
                      >
                        {isArticleLinkAvailable ? (
                          <CustomLink
                            href={`/${locale}/${pages.PRODUCT}/${itemId}?art=${
                              cellData.data[col.accessor]
                            }&searchTerm=${cellData.data[col.accessor]}`}
                          >
                            <Link textDecoration="underline" isExternal={areLinksExternal}>
                              {cellData.data[col.accessor]}
                            </Link>
                          </CustomLink>
                        ) : isSPTLinkAvailable ? (
                          <>
                            <Text as="span" color="gray.800">
                              {`${t("to-the-spare-parts-table")} `}
                            </Text>
                            <CustomTooltip label={t("open-related-spare-parts")}>
                              <Box display="initial">
                                <CustomLink href={`/${locale}/${pages.SPARE_PART}/${itemId}`}>
                                  <Link textDecoration="underline" isExternal={areLinksExternal}>
                                    {cellData.data[col.accessor]}
                                  </Link>
                                </CustomLink>
                              </Box>
                            </CustomTooltip>
                          </>
                        ) : isMedia ? (
                          <AssetsCell
                            documents={cellData?.documents ?? []}
                            videos={cellData?.videos ?? []}
                            articleNumber={cellData.data[sparePartsAttributes.articleNumber]}
                            toggleModalBackdropEventListeners={updateHoverDisabled}
                          />
                        ) : (
                          cellData.data[col.accessor]
                        )}
                      </Td>
                    )
                  })}
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default SparePartsTable
